import { create } from 'zustand';

export interface Participant {
  id: string;
  userId?: string;
  displayName: string;
  isHost: boolean;
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenShareEnabled: boolean;
  raisedHand: boolean;
  isSpeaking: boolean;
  speakingStartTime?: number; // Timestamp when started speaking (for prioritization)
  stream?: MediaStream;
  screenStream?: MediaStream;
  audioProducerId?: string;
  videoProducerId?: string;
  screenProducerId?: string;
}

interface ParticipantsState {
  participants: Map<string, Participant>;
  localParticipant: Participant | null;
  
  addParticipant: (participant: Participant) => void;
  removeParticipant: (participantId: string) => void;
  updateParticipant: (participantId: string, updates: Partial<Participant>) => void;
  setLocalParticipant: (participant: Participant) => void;
  updateLocalParticipant: (updates: Partial<Participant>) => void;
  setParticipantStream: (participantId: string, stream: MediaStream) => void;
  setParticipantScreenStream: (participantId: string, stream: MediaStream | undefined) => void;
  clear: () => void;
}

export const useParticipantsStore = create<ParticipantsState>((set) => ({
  participants: new Map(),
  localParticipant: null,

  addParticipant: (participant) =>
    set((state) => {
      const newParticipants = new Map(state.participants);
      newParticipants.set(participant.id, participant);
      return { participants: newParticipants };
    }),

  removeParticipant: (participantId) =>
    set((state) => {
      const newParticipants = new Map(state.participants);
      newParticipants.delete(participantId);
      return { participants: newParticipants };
    }),

  updateParticipant: (participantId, updates) =>
    set((state) => {
      const newParticipants = new Map(state.participants);
      const participant = newParticipants.get(participantId);
      if (participant) {
        newParticipants.set(participantId, { ...participant, ...updates });
      }
      return { participants: newParticipants };
    }),

  setLocalParticipant: (participant) =>
    set({ localParticipant: participant }),

  updateLocalParticipant: (updates) =>
    set((state) => ({
      localParticipant: state.localParticipant
        ? { ...state.localParticipant, ...updates }
        : null,
    })),

  setParticipantStream: (participantId, stream) =>
    set((state) => {
      const newParticipants = new Map(state.participants);
      const participant = newParticipants.get(participantId);
      if (participant) {
        newParticipants.set(participantId, { ...participant, stream });
      }
      return { participants: newParticipants };
    }),

  setParticipantScreenStream: (participantId, stream) =>
    set((state) => {
      const newParticipants = new Map(state.participants);
      const participant = newParticipants.get(participantId);
      if (participant) {
        newParticipants.set(participantId, { ...participant, screenStream: stream });
      }
      return { participants: newParticipants };
    }),

  clear: () =>
    set({
      participants: new Map(),
      localParticipant: null,
    }),
}));
